import useUserIs from '/hooks/useUserIs';
import userHasPermission from '/lib/utils/userHasPermission';
import { useUserState } from '/store/userStore';
import { PermissionType } from '/types/permissionTypes';
import { UserAbilities, UserAbilitiesObject } from '/types/UserAbilityTypes';
import { AnyUserType } from '/types/userTypes';

/*
 This hook accepts _user as optional argument
 In some cases - like when used in combination with withAuth component
 it's usefull to have user state right away before store hydration
 (withAuth provides user on first render so we want to use it before store hydrates)
 */
export default function useUserCan(_user?: AnyUserType): UserAbilitiesObject {
    const user = useUserState((state) => state.user);
    const actualUser = _user || user;

    const userIs = useUserIs(actualUser);

    return {
        [UserAbilities.CHANGE_PROJECT_CASEWORKER]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
        [UserAbilities.CHANGE_PROJECT_ELECTRICIAN]:
            userIs.globalAdmin || userIs.caseworker || userIs.contractorAdmin,
        [UserAbilities.EDIT_PROJECT_CUSTOMER_INFO]:
            userIs.globalAdmin ||
            userIs.caseworker ||
            userIs.customerService ||
            userIs.storeEmployee,
        [UserAbilities.CREATE_CLAIM]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
        [UserAbilities.CANCEL_PROJECT]:
            userIs.globalAdmin ||
            userIs.caseworker ||
            userIs.customerService ||
            userIs.storeEmployee,
        [UserAbilities.CHANGE_PROJECT_INSTALLATION_DATE]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
        [UserAbilities.CHANGE_VIDEO_INSPECTION_DATE]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
        [UserAbilities.SEE_ADDITIONAL_ELECTRICIAN_JOB_INFO]:
            userIs.globalAdmin ||
            userIs.caseworker ||
            userIs.electrician ||
            userIs.contractorAdmin,
        [UserAbilities.SEE_JOB_WORK_HOURS]:
            !!user &&
            !userIs.electrician &&
            !userIs.contractorAdmin &&
            !userIs.customer,
        [UserAbilities.SEE_JOB_MATERIAL_LIST]:
            userIs.globalAdmin ||
            userIs.caseworker ||
            userIs.electrician ||
            userIs.contractorAdmin ||
            userIs.customerService ||
            userIs.storeEmployee,
        [UserAbilities.SEE_PROJECT_WORK_HOURS]:
            !userIs.electrician ||
            userHasPermission(
                actualUser,
                PermissionType.VIEW_ESTIMATED_WORK_HOURS
            ),
        [UserAbilities.SEE_MATERIAL_LIST_PRICES]:
            !userIs.electrician && !userIs.contractorAdmin,
        [UserAbilities.EDIT_TEMPLATE_JOB_IMAGES]:
            !userIs.electrician && !userIs.contractorAdmin,
        [UserAbilities.EDIT_TEMPLATE_JOB_MATERIAL_LIST]:
            !userIs.electrician && !userIs.contractorAdmin,
        [UserAbilities.CHANGE_PHYSICAL_INSPECTION_DATE]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
        [UserAbilities.COMPLETE_PHYSICAL_INSPECTION]:
            userIs.globalAdmin ||
            userIs.caseworker ||
            userIs.contractorAdmin ||
            userIs.electrician,
        [UserAbilities.CREATE_OFFER_FROM_VIDEO_INSPECTION]:
            userIs.globalAdmin || userIs.caseworker,
        [UserAbilities.MARK_APPLICATION_AS_SENT]:
            userIs.globalAdmin ||
            userIs.caseworker ||
            userIs.contractorAdmin ||
            userIs.electrician,
        [UserAbilities.MARK_APPLICATION_AS_APPROVED_OR_DECLINED]:
            userIs.globalAdmin ||
            userIs.caseworker ||
            userIs.contractorAdmin ||
            userIs.electrician,
        [UserAbilities.COMPLETE_PROJECT]:
            userIs.globalAdmin ||
            userIs.caseworker ||
            userIs.contractorAdmin ||
            userIs.electrician,
        [UserAbilities.SEE_PROJECT_ACTIVITY_LOG]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
        [UserAbilities.SEE_EVALUATION_SUMMARY]:
            userIs.globalAdmin ||
            userIs.caseworker ||
            userIs.customerService ||
            userIs.storeEmployee,
        [UserAbilities.SEE_JOB_COMMENT_FOR_ELECTRICIAN]:
            userIs.globalAdmin ||
            userIs.caseworker ||
            userIs.contractorAdmin ||
            userIs.electrician,
        [UserAbilities.STOP_PRE_PAYMENT_PROCESS]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
        [UserAbilities.FILTER_PROJECTS_BY_CONTRACTOR]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
        [UserAbilities.FILTER_PROJECTS_BY_ELECTRICIAN]:
            userIs.globalAdmin ||
            userIs.contractorAdmin ||
            userIs.caseworker ||
            userIs.customerService,
        [UserAbilities.FILTER_PROJECTS_BY_CASEWORKER]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
        [UserAbilities.FILTER_PROJECTS_BY_STAGE_AND_STATUS]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
        [UserAbilities.SORT_PROJECTS_TABLE]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
        [UserAbilities.CREATE_PROJECT]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
        [UserAbilities.EDIT_PROJECT_ELECTRICIAN_INFO]:
            userIs.globalAdmin || userIs.caseworker,
        [UserAbilities.EDIT_CUSTOMER_INFO]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
        [UserAbilities.MODIFY_PROJECT_SELLER]: userIs.globalAdmin,
        [UserAbilities.MAKE_CHANGES_TO_INITIAL_ORDER]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
        [UserAbilities.SYNC_PROJECT_SALE]:
            userIs.globalAdmin || userIs.caseworker || userIs.customerService,
    };
}
